import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon, DollarSign, Shield, Gamepad2, Wrench, MessageSquare,
  Loader2, Save, AlertTriangle, Lock, ServerCog, CheckCircle2,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Switch } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Textarea } from '@ouropix/ui';
import { Skeleton } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@ouropix/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ouropix/ui';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { unwrapList, apiErrorMessage } from '@/lib/api-helpers';
import AdminPageHeader from '@/components/layout/PageHeader';
import { AllowedEmailsEditor } from '@/components/admin-ops/AllowedEmailsEditor';
import { Admin2FASetup } from '@/components/admin-ops/Admin2FASetup';
import { useAuth } from '@/hooks/useAuth';

// ── Route search ───────────────────────────────────────────────

type TabId = 'geral' | 'seguranca' | 'financeiro' | 'jogos';

interface RouteSearch { tab?: TabId; }

export const Route = createFileRoute('/_admin/configuracoes')({
  validateSearch: (search: Record<string, unknown>): RouteSearch => {
    const VALID: TabId[] = ['geral', 'seguranca', 'financeiro', 'jogos'];
    const t = search.tab as TabId;
    return { tab: VALID.includes(t) ? t : undefined };
  },
  component: ConfiguracoesPage,
});

// ── Types ─────────────────────────────────────────────────────

interface SettingRow { key: string; value: unknown; }
interface MaintenanceSettings { enabled: boolean; message: string; }
interface DepositLimits { min: number; max: number; }
interface WithdrawalLimits { min: number; max: number; fee_percent: number; }
interface SecuritySettings { max_login_attempts: number; block_duration_hours: number; attempt_window_minutes: number; }
interface DemoLimits { daily_plays: number; }
interface SupportContact { whatsapp: string; email: string; }
interface GamesEnabled { scratch_cards: boolean; slots: boolean; wheel: boolean; mystery_boxes: boolean; casino: boolean; }
interface SiteLockState { enabled: boolean; has_password: boolean; cookie_expiry_hours: number; }
interface SystemModeResponse { mode: 'dev' | 'prod'; updated_at: string; updated_by: { id: string; full_name: string } | null; }

// ── Hook: system mode ──────────────────────────────────────────

function useSystemMode() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<SystemModeResponse>({
    queryKey: ['system-mode-admin'],
    queryFn: () => api.get('/admin/system/mode').then((r) => r.data),
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });

  const patchMode = useMutation({
    mutationFn: (mode: 'dev' | 'prod') =>
      api.patch('/admin/system/mode', { mode }).then((r) => r.data),
    onSuccess: () => {
      toast.success('Modo do sistema atualizado!');
      queryClient.invalidateQueries({ queryKey: ['system-mode-admin'] });
      queryClient.invalidateQueries({ queryKey: ['system-mode-topbar'] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao alterar modo do sistema')),
  });

  return { data, isLoading, patchMode };
}

// ── Hook: settings ─────────────────────────────────────────────

function useAdminSettings() {
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useQuery<SettingRow[]>({
    queryKey: ['admin-settings'],
    queryFn: () => api.get('/admin/settings').then((r) => unwrapList(r.data, 'settings')),
    retry: false,
    throwOnError: false,
  });

  function getSettingValue<T>(key: string): T | null {
    const row = settings?.find((s) => s.key === key);
    if (!row) return null;
    return row.value as T;
  }

  const updateSetting = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.put(`/admin/settings/${key}`, { value }).then((r) => r.data),
    onSuccess: () => {
      toast.success('Configuração salva!');
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao salvar configuração')),
  });

  return { settings, isLoading, getSettingValue, updateSetting };
}

// ── Page ──────────────────────────────────────────────────────

function ConfiguracoesPage() {
  const { settings, isLoading, getSettingValue, updateSetting } = useAdminSettings();
  const { data: systemMode, isLoading: modeLoading, patchMode } = useSystemMode();
  const { can } = useAuth();
  const isSuperadmin = can('edit_settings');
  const [confirmProdOpen, setConfirmProdOpen] = useState(false);

  const search = useSearch({ from: '/_admin/configuracoes' });
  const navigate = useNavigate();
  const activeTab: TabId = search.tab ?? 'geral';

  const handleTabChange = (value: string) => {
    navigate({ to: '/configuracoes', search: { tab: value as TabId }, replace: true });
  };

  // Site Lock
  const { data: siteLock, refetch: refetchSiteLock } = useQuery<SiteLockState>({
    queryKey: ['site-lock-settings'],
    queryFn: () => api.get('/admin/settings/site-lock').then((r) => r.data),
    retry: false,
    throwOnError: false,
  });
  const [siteLockEnabled, setSiteLockEnabled] = useState(false);
  const [siteLockPassword, setSiteLockPassword] = useState('');
  const [siteLockExpiry, setSiteLockExpiry] = useState(24);

  useEffect(() => {
    if (siteLock) {
      setSiteLockEnabled(siteLock.enabled);
      setSiteLockExpiry(siteLock.cookie_expiry_hours);
    }
  }, [siteLock]);

  const updateSiteLock = useMutation({
    mutationFn: (opts?: { enabled?: boolean; password?: string; cookie_expiry_hours?: number }) =>
      api.put('/admin/settings/site-lock', {
        enabled: opts?.enabled ?? siteLockEnabled,
        ...((opts?.password ?? siteLockPassword) ? { password: opts?.password ?? siteLockPassword } : {}),
        cookie_expiry_hours: opts?.cookie_expiry_hours ?? siteLockExpiry,
      }).then((r) => r.data),
    onSuccess: () => {
      refetchSiteLock();
      setSiteLockPassword('');
      toast.success('Acesso ao site atualizado!');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao salvar acesso ao site')),
  });

  // States for each setting group
  const [maintenance, setMaintenance] = useState<MaintenanceSettings>({ enabled: false, message: '' });
  const [depositLimits, setDepositLimits] = useState<DepositLimits>({ min: 10, max: 5000 });
  const [withdrawalLimits, setWithdrawalLimits] = useState<WithdrawalLimits>({ min: 10, max: 5000, fee_percent: 0 });
  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({ max_login_attempts: 5, block_duration_hours: 24, attempt_window_minutes: 15 });
  const [demoLimits, setDemoLimits] = useState<DemoLimits>({ daily_plays: 5 });
  const [supportContact, setSupportContact] = useState<SupportContact>({ whatsapp: '', email: '' });
  const [gamesEnabled, setGamesEnabled] = useState<GamesEnabled>({ scratch_cards: true, slots: true, wheel: true, mystery_boxes: true, casino: true });

  useEffect(() => {
    if (!settings) return;
    const m = getSettingValue<MaintenanceSettings>('maintenance_mode');
    if (m) setMaintenance(m);
    const d = getSettingValue<DepositLimits>('deposit_limits');
    if (d) setDepositLimits(d);
    const w = getSettingValue<WithdrawalLimits>('withdrawal_limits');
    if (w) setWithdrawalLimits(w);
    const sec = getSettingValue<SecuritySettings>('security_settings');
    if (sec) setSecuritySettings(sec);
    const demo = getSettingValue<DemoLimits>('demo_limits');
    if (demo) setDemoLimits(demo);
    const sup = getSettingValue<SupportContact>('support_contact');
    if (sup) setSupportContact(sup);
    const games = getSettingValue<GamesEnabled>('games_enabled');
    if (games) setGamesEnabled(games);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const handleSave = (key: string, value: unknown) => {
    updateSetting.mutate({ key, value });
  };

  // ── Acesso ao Site (unifica Manutenção + Site Lock — estados mutuamente
  // exclusivos). Aplica em tempo real ao selecionar o estado.
  type AccessMode = 'open' | 'maintenance' | 'locked';
  const accessMode: AccessMode = siteLockEnabled ? 'locked' : maintenance.enabled ? 'maintenance' : 'open';

  const applyAccessMode = (mode: AccessMode) => {
    if (mode === accessMode) return;
    if (mode === 'open') {
      if (maintenance.enabled) { const v = { ...maintenance, enabled: false }; setMaintenance(v); updateSetting.mutate({ key: 'maintenance_mode', value: v }); }
      if (siteLockEnabled) { setSiteLockEnabled(false); updateSiteLock.mutate({ enabled: false }); }
    } else if (mode === 'maintenance') {
      const v = { ...maintenance, enabled: true }; setMaintenance(v); updateSetting.mutate({ key: 'maintenance_mode', value: v });
      if (siteLockEnabled) { setSiteLockEnabled(false); updateSiteLock.mutate({ enabled: false }); }
    } else {
      // Bloqueado por senha exige senha definida (existente ou digitada agora).
      if (!siteLock?.has_password && !siteLockPassword) {
        toast.error('Defina uma senha no campo abaixo antes de bloquear o site.');
        return;
      }
      setSiteLockEnabled(true); updateSiteLock.mutate({ enabled: true, password: siteLockPassword || undefined });
      if (maintenance.enabled) { const v = { ...maintenance, enabled: false }; setMaintenance(v); updateSetting.mutate({ key: 'maintenance_mode', value: v }); }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 md:p-6">
        <Skeleton className="h-10 w-64" />
        <div className="space-y-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-5xl">
      <AdminPageHeader
        icon={SettingsIcon}
        title="Configurações"
        description="Gerencie as configurações da plataforma"
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full justify-start gap-1 h-auto flex-wrap">
          <TabsTrigger value="geral" className="gap-2">
            <Wrench className="h-4 w-4" />
            Geral
          </TabsTrigger>
          <TabsTrigger value="seguranca" className="gap-2">
            <Shield className="h-4 w-4" />
            Segurança
          </TabsTrigger>
          <TabsTrigger value="financeiro" className="gap-2">
            <DollarSign className="h-4 w-4" />
            Financeiro
          </TabsTrigger>
          <TabsTrigger value="jogos" className="gap-2">
            <Gamepad2 className="h-4 w-4" />
            Jogos
          </TabsTrigger>
        </TabsList>

        {/* ── Tab: Geral ──────────────────────────────────────── */}
        <TabsContent value="geral" className="space-y-6 mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Acesso ao Site (unifica Manutenção + Site Lock) */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <div className="p-2 rounded-lg bg-orange-500/10">
                    <Lock className="h-4 w-4 text-orange-500" />
                  </div>
                  Acesso ao Site
                </CardTitle>
                <CardDescription>Quem pode acessar o site público (aplica em tempo real)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2" role="group" aria-label="Estado de acesso ao site">
                  {([
                    { id: 'open', label: 'Aberto', hint: 'Normal' },
                    { id: 'maintenance', label: 'Manutenção', hint: 'Com mensagem' },
                    { id: 'locked', label: 'Bloqueado', hint: 'Com senha' },
                  ] as { id: AccessMode; label: string; hint: string }[]).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => applyAccessMode(opt.id)}
                      className={[
                        'rounded-lg border p-2 text-center transition-colors',
                        accessMode === opt.id
                          ? (opt.id === 'open' ? 'border-green-500/50 bg-green-500/10' : 'border-orange-500/50 bg-orange-500/10')
                          : 'bg-muted/50 hover:bg-muted',
                      ].join(' ')}
                    >
                      <span className="block text-sm font-medium">{opt.label}</span>
                      <span className="block text-[10px] text-muted-foreground">{opt.hint}</span>
                    </button>
                  ))}
                </div>

                {accessMode === 'open' && (
                  <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700">
                    <CheckCircle2 className="inline h-4 w-4 mr-1" />
                    Site aberto ao público normalmente.
                  </div>
                )}

                {accessMode === 'maintenance' && (
                  <div className="space-y-2">
                    <Label htmlFor="maintenance-message" className="text-sm font-medium">Mensagem de Manutenção</Label>
                    <Textarea
                      id="maintenance-message"
                      placeholder="Mensagem exibida durante manutenção..."
                      value={maintenance.message}
                      onChange={(e) => setMaintenance({ ...maintenance, message: e.target.value })}
                      rows={3}
                      className="resize-none"
                    />
                    <Button size="sm" onClick={() => handleSave('maintenance_mode', { ...maintenance, enabled: true })} disabled={updateSetting.isPending} className="w-full">
                      {updateSetting.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Salvar mensagem
                    </Button>
                  </div>
                )}

                {accessMode === 'locked' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                      <AlertTriangle className="inline h-4 w-4 mr-1" />
                      O site está protegido por senha (pré-launch).
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="site-lock-password" className="text-sm font-medium">
                        {siteLock?.has_password ? 'Nova senha (deixe em branco para manter)' : 'Definir senha'}
                      </Label>
                      <Input id="site-lock-password" type="password" placeholder={siteLock?.has_password ? '••••••••' : 'Digite uma senha'} value={siteLockPassword} onChange={(e) => setSiteLockPassword(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="site-lock-expiry" className="text-sm font-medium">Expiração do cookie (horas)</Label>
                      <Input id="site-lock-expiry" type="number" min={1} max={8760} value={siteLockExpiry} onChange={(e) => setSiteLockExpiry(Number(e.target.value))} />
                    </div>
                    <Button size="sm" onClick={() => updateSiteLock.mutate({ enabled: true, password: siteLockPassword || undefined })} disabled={updateSiteLock.isPending} className="w-full" variant="destructive">
                      {updateSiteLock.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Salvar senha / expiração
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contato Suporte */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <MessageSquare className="h-4 w-4 text-green-500" />
                  </div>
                  Contato de Suporte
                </CardTitle>
                <CardDescription>Informações de contato para usuários</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="support-whatsapp" className="text-sm font-medium">WhatsApp</Label>
                  <Input id="support-whatsapp" placeholder="5511999999999" value={supportContact.whatsapp} onChange={(e) => setSupportContact({ ...supportContact, whatsapp: e.target.value })} />
                  <p className="text-xs text-muted-foreground">Número com código do país (sem espaços ou traços)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support-email" className="text-sm font-medium">Email</Label>
                  <Input id="support-email" type="email" placeholder="suporte@exemplo.com" value={supportContact.email} onChange={(e) => setSupportContact({ ...supportContact, email: e.target.value })} />
                </div>
                <Button size="sm" onClick={() => handleSave('support_contact', supportContact)} disabled={updateSetting.isPending} className="w-full">
                  {updateSetting.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salvar Alterações
                </Button>
              </CardContent>
            </Card>

            {/* Modo do Sistema */}
            {modeLoading ? (
              <Skeleton className="h-48" />
            ) : (
              <Card className={systemMode?.mode === 'dev' ? 'border-orange-500/50 bg-orange-500/5' : 'border-green-500/50 bg-green-500/5'}>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base font-semibold">
                      <div className={`p-2 rounded-lg ${systemMode?.mode === 'dev' ? 'bg-orange-500/20' : 'bg-green-500/20'}`}>
                        <ServerCog className={`h-4 w-4 ${systemMode?.mode === 'dev' ? 'text-orange-500' : 'text-green-600'}`} />
                      </div>
                      Ambiente de Pagamentos
                    </CardTitle>
                    {systemMode?.mode === 'dev' ? (
                      <Badge variant="outline" className="bg-orange-500/20 text-orange-600 border-orange-500/30 animate-pulse">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        MODO TESTE
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-green-500/20 text-green-700 border-green-500/30">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        MODO PRODUCAO
                      </Badge>
                    )}
                  </div>
                  <CardDescription>
                    Controla se a plataforma opera com pagamentos reais (EFI) ou simulados
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {systemMode?.mode === 'dev' && (
                    <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-sm text-orange-700">
                      <AlertTriangle className="inline h-4 w-4 mr-1" />
                      Em modo DEV: saques bloqueados, depositos simulados, transacoes marcadas como teste.
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div>
                      <Label htmlFor="system-mode-toggle" className="text-sm font-medium">
                        {systemMode?.mode === 'dev' ? 'Ativar Modo Producao' : 'Modo Producao ativo'}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {isSuperadmin ? 'Apenas superadmin pode alterar' : 'Somente leitura'}
                      </p>
                    </div>
                    <Switch
                      id="system-mode-toggle"
                      checked={systemMode?.mode === 'prod'}
                      disabled={!isSuperadmin || patchMode.isPending}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setConfirmProdOpen(true);
                        } else {
                          patchMode.mutate('dev');
                        }
                      }}
                    />
                  </div>
                  {systemMode?.updated_at && (
                    <p className="text-xs text-muted-foreground">
                      Ultima alteracao:{' '}
                      {new Date(systemMode.updated_at).toLocaleString('pt-BR')}{' '}
                      {systemMode.updated_by ? `por ${systemMode.updated_by.full_name}` : ''}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ── Tab: Segurança ──────────────────────────────────── */}
        <TabsContent value="seguranca" className="space-y-6 mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <Shield className="h-4 w-4 text-red-500" />
                  </div>
                  Controle de Acesso
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="max-login" className="text-sm font-medium">Max. tentativas de login</Label>
                  <Input id="max-login" type="number" min={1} value={securitySettings.max_login_attempts} onChange={(e) => setSecuritySettings({ ...securitySettings, max_login_attempts: Number(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="block-duration" className="text-sm font-medium">Duração do bloqueio (horas)</Label>
                  <Input id="block-duration" type="number" min={0} value={securitySettings.block_duration_hours} onChange={(e) => setSecuritySettings({ ...securitySettings, block_duration_hours: Number(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="attempt-window" className="text-sm font-medium">Janela de tentativas (minutos)</Label>
                  <Input id="attempt-window" type="number" min={1} value={securitySettings.attempt_window_minutes} onChange={(e) => setSecuritySettings({ ...securitySettings, attempt_window_minutes: Number(e.target.value) })} />
                </div>
                <Button size="sm" onClick={() => handleSave('security_settings', securitySettings)} disabled={updateSetting.isPending}>
                  {updateSetting.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salvar Alterações
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Whitelist de Admins */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-1 bg-primary rounded-full" />
              <h2 className="text-base font-semibold">Whitelist de Admins</h2>
            </div>
            <AllowedEmailsEditor />
          </div>

          {/* 2FA do admin logado */}
          <Admin2FASetup />
        </TabsContent>

        {/* ── Tab: Financeiro ─────────────────────────────────── */}
        <TabsContent value="financeiro" className="space-y-6 mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <div className="p-2 rounded-lg bg-emerald-500/10">
                    <DollarSign className="h-4 w-4 text-emerald-500" />
                  </div>
                  Limites de Depósito
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="deposit-min" className="text-sm font-medium">Mínimo (R$)</Label>
                    <Input id="deposit-min" type="number" min={0} value={depositLimits.min} onChange={(e) => setDepositLimits({ ...depositLimits, min: Number(e.target.value) })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deposit-max" className="text-sm font-medium">Máximo (R$)</Label>
                    <Input id="deposit-max" type="number" min={0} value={depositLimits.max} onChange={(e) => setDepositLimits({ ...depositLimits, max: Number(e.target.value) })} />
                  </div>
                </div>
                <Button size="sm" onClick={() => handleSave('deposit_limits', depositLimits)} disabled={updateSetting.isPending} className="w-full">
                  {updateSetting.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salvar Alterações
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <DollarSign className="h-4 w-4 text-blue-500" />
                  </div>
                  Limites de Saque
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="withdraw-min" className="text-sm font-medium">Mínimo (R$)</Label>
                    <Input id="withdraw-min" type="number" min={0} value={withdrawalLimits.min} onChange={(e) => setWithdrawalLimits({ ...withdrawalLimits, min: Number(e.target.value) })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="withdraw-max" className="text-sm font-medium">Máximo (R$)</Label>
                    <Input id="withdraw-max" type="number" min={0} value={withdrawalLimits.max} onChange={(e) => setWithdrawalLimits({ ...withdrawalLimits, max: Number(e.target.value) })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="withdraw-fee" className="text-sm font-medium">Taxa (%)</Label>
                  <Input id="withdraw-fee" type="number" min={0} max={100} step={0.1} value={withdrawalLimits.fee_percent} onChange={(e) => setWithdrawalLimits({ ...withdrawalLimits, fee_percent: Number(e.target.value) })} />
                </div>
                <Button size="sm" onClick={() => handleSave('withdrawal_limits', withdrawalLimits)} disabled={updateSetting.isPending} className="w-full">
                  {updateSetting.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salvar Alterações
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Tab: Jogos ──────────────────────────────────────── */}
        <TabsContent value="jogos" className="space-y-6 mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Gamepad2 className="h-4 w-4 text-purple-500" />
                  </div>
                  Limite de Demo
                </CardTitle>
                <CardDescription>Jogadas demo permitidas por dia por usuário</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="demo-limit" className="text-sm font-medium">Jogadas por dia</Label>
                  <Input id="demo-limit" type="number" min={0} value={demoLimits.daily_plays} onChange={(e) => setDemoLimits({ daily_plays: Number(e.target.value) })} />
                  <p className="text-xs text-muted-foreground">0 = ilimitado</p>
                </div>
                <Button size="sm" onClick={() => handleSave('demo_limits', demoLimits)} disabled={updateSetting.isPending} className="w-full">
                  {updateSetting.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salvar Alterações
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <div className="p-2 rounded-lg bg-yellow-500/10">
                    <Gamepad2 className="h-4 w-4 text-yellow-500" />
                  </div>
                  Jogos Habilitados
                </CardTitle>
                <CardDescription>Ativar ou desativar tipos de jogos</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      { id: 'scratch_cards', label: 'Raspadinhas' },
                      { id: 'slots', label: 'Slots' },
                      { id: 'wheel', label: 'Roleta' },
                      { id: 'mystery_boxes', label: 'Caixas' },
                      { id: 'casino', label: 'Cassino' },
                    ] as { id: keyof GamesEnabled; label: string }[]
                  ).map((game) => (
                    <div key={game.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                      <Label htmlFor={`game-${game.id}`} className="text-sm font-medium cursor-pointer">{game.label}</Label>
                      <Switch id={`game-${game.id}`} checked={gamesEnabled[game.id]} onCheckedChange={(checked) => setGamesEnabled({ ...gamesEnabled, [game.id]: checked })} />
                    </div>
                  ))}
                </div>
                <Button size="sm" onClick={() => handleSave('games_enabled', gamesEnabled)} disabled={updateSetting.isPending} className="w-full">
                  {updateSetting.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salvar Alterações
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Confirmação de troca para prod */}
      <AlertDialog open={confirmProdOpen} onOpenChange={setConfirmProdOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ativar Modo Producao?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza? Pagamentos reais via EFI serao habilitados. Saques e depositos
              passarao a processar valores reais. Esta acao afeta todos os usuarios imediatamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              onClick={() => patchMode.mutate('prod')}
            >
              Sim, ativar producao
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
